import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useCloudAuth } from '../context/CloudAuthContext';

const BusinessProfileContext = createContext(null);

export const useBusinessProfile = () => {
  const context = useContext(BusinessProfileContext);
  if (!context) {
    throw new Error('useBusinessProfile must be used within BusinessProfileProvider');
  }
  return context;
};

export const BusinessProfileProvider = ({ children }) => {
  const { fetchApi, token } = useCloudAuth();
  const [profiles, setProfiles] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load profiles when authenticated
  const loadProfiles = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await fetchApi('/api/cloud/business-profiles');
      
      if (res.success && res.data.profiles) {
        setProfiles(res.data.profiles);
        
        // Get saved profile from localStorage or use default
        const savedProfileId = localStorage.getItem('currentBusinessProfileId');
        let profile = null;
        
        if (savedProfileId) {
          profile = res.data.profiles.find(p => p.id === savedProfileId);
        }
        
        if (!profile) {
          // Use default profile
          profile = res.data.profiles.find(p => p.isDefault) || res.data.profiles[0];
        }
        
        if (profile) {
          setCurrentProfile(profile);
          localStorage.setItem('currentBusinessProfileId', profile.id);
        }
      }
    } catch (err) {
      console.error('Failed to load business profiles:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchApi, token]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  // Switch to a different profile
  const switchProfile = useCallback((profileId) => {
    const profile = profiles.find(p => p.id === profileId);
    if (profile) {
      setCurrentProfile(profile);
      localStorage.setItem('currentBusinessProfileId', profileId);
    }
  }, [profiles]);

  // Create a new profile
  const createProfile = useCallback(async (name, description = '') => {
    try {
      const res = await fetchApi('/api/cloud/business-profiles', {
        method: 'POST',
        body: JSON.stringify({ name, description })
      });
      
      if (res.success) {
        await loadProfiles();
        return res.data;
      }
    } catch (err) {
      console.error('Failed to create profile:', err);
      throw err;
    }
  }, [fetchApi, loadProfiles]);

  // Update current profile settings
  const updateProfileSettings = useCallback(async (settings) => {
    if (!currentProfile) return;
    
    try {
      const res = await fetchApi(`/api/cloud/business-profiles/${currentProfile.id}/settings`, {
        method: 'PUT',
        body: JSON.stringify(settings)
      });
      
      if (res.success) {
        // Update local state
        setCurrentProfile(prev => ({
          ...prev,
          settings: { ...prev.settings, ...settings }
        }));
        await loadProfiles();
      }
      
      return res;
    } catch (err) {
      console.error('Failed to update profile settings:', err);
      throw err;
    }
  }, [fetchApi, currentProfile, loadProfiles]);

  // Delete a profile
  const deleteProfile = useCallback(async (profileId) => {
    try {
      const res = await fetchApi(`/api/cloud/business-profiles/${profileId}`, {
        method: 'DELETE'
      });
      
      if (res.success) {
        // If we deleted the current profile, switch to default
        if (currentProfile?.id === profileId) {
          localStorage.removeItem('currentBusinessProfileId');
        }
        await loadProfiles();
      }
      
      return res;
    } catch (err) {
      console.error('Failed to delete profile:', err);
      throw err;
    }
  }, [fetchApi, currentProfile, loadProfiles]);

  const value = {
    profiles,
    currentProfile,
    loading,
    switchProfile,
    createProfile,
    updateProfileSettings,
    deleteProfile,
    refreshProfiles: loadProfiles
  };

  return (
    <BusinessProfileContext.Provider value={value}>
      {children}
    </BusinessProfileContext.Provider>
  );
};

export default BusinessProfileContext;
